import { Box, Card, CardContent, Container, CssBaseline, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import CurrentLottery from 'src/content/dashboards/Lottery/CurrentLottery';
import { Lottery } from 'src/util/types';
import config from '../../../config';
import { ThemeProvider } from '@mui/material/styles';
import { standaloneTheme } from 'src/components/LotteryView/theme';

type LocationState = {
  luckyNumber?: string;
  lotteryId?: string;
};

export default function LuckyNumberPage() {
  const location = useLocation();
  const { luckyNumber, lotteryId }: LocationState = (location.state as LocationState) || {};
  const [lottery, setLottery] = useState<Lottery | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lotteryId) {
      setLottery(null);
      return;
    }
    let isMounted = true;
    axios
      .get(`${config.apiUrl}/api/public/lottery/${lotteryId}`)
      .then((res) => {
        if (!isMounted) return;
        const payload = res?.data?.content ?? res?.data;
        setLottery(payload || null);
        setError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.log("Couldn't fetch lottery data", err);
        setLottery(null);
        setError('Aktuell gibt es keine aktive Lotterie');
      });
    return () => {
      isMounted = false;
    };
  }, [lotteryId]);

  const hasLuckyNumber = useMemo(() => Boolean(luckyNumber), [luckyNumber]);

  return (
    <ThemeProvider theme={standaloneTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at top, rgba(255,136,0,0.1), transparent 60%)',
            filter: 'blur(80px)'
          }
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Card
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(10,10,10,0.85)',
              boxShadow: '0 30px 60px rgba(0,0,0,0.6)'
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: { xs: 3, md: 5 } }}>
              <Typography variant="body1" sx={{ letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>
                Dein persönlicher Glückscode
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  mt: 2,
                  fontWeight: 700,
                  color: '#fff',
                  textShadow: '0 15px 35px rgba(0,0,0,0.7)',
                  minHeight: 64
                }}
              >
                {hasLuckyNumber ? luckyNumber : 'Kein Code vorhanden'}
              </Typography>
              {!hasLuckyNumber && (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Die Teilnahme war leider nicht erfolgreich. Bitte versuche es erneut.
                </Typography>
              )}

              {lottery ? (
                <Box mt={4}>
                  <CurrentLottery lotteryData={lottery} />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary' }}>
                  {error || 'Keine Lotterie gefunden.'}
                </Typography>
              )}

              
            </CardContent>
          </Card>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
